import { Link as FluentLink, Spinner } from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  ArrowSortRegular,
  CheckboxPersonRegular,
  DismissFilled,
  HistoryFilled,
  HistoryRegular,
  OpenRegular,
  PeopleRegular,
  RenameRegular,
  SettingsRegular,
} from '@fluentui/react-icons';

import { MenuProvider as LinkMenuProvider } from '../contexts/MenuContext.context';
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import DocumentContext from '../contexts/DocumentContext.context';
import {
  EItemScreenMode,
  getPdfFilename,
  getVersionId,
  isTDraft,
  ItemPage,
  NavOverview,
  PERIODIC_REVIEW_QUERY_PARAM,
  PERIODIC_REVIEW_QUERY_VALUE,
  useExternalLinkNavAction,
  useItemScreenStore,
  PeriodicReviewDialog,
} from '@modules/items';
import { AccessType, AccessDialog, TAccessDialogState, useCanEdit } from '@modules/access';
import Header from './header/Header';
import Content from './Content';
import { TMap } from '@shared/types';
import { useNavigate } from 'react-router-dom';
import { ToastContext, LayoutContext } from '@shared/contexts';
import { downloadFile } from '@modules/files';
import {
  CreateApprovalDialog,
  ViewApprovalDialog,
  UpdateApprovalDialog,
  ApprovalStatus,
  TViewApprovalDialogState,
  TUpdateApprovalDialogState,
} from '@modules/approvals';
import config from '@config/index';

import { TOverflowButtonParam, OptionsDialog } from '@shared/components';
import { TOptionsDialogState } from '@shared/types';
import { useSearchParams } from '@shared/hooks';
import { useExport as useExportDoc } from '../api';
import ItemConfigDialog, { EDocumentConfigTab } from './dialogs/ItemConfigDialog/ItemConfigDialog';
import { TDocumentAttachment, TDraft, TVersion } from '../types';
import { SignInContext } from '@modules/users';
import { useImpactAnalysisNavAction } from '@modules/impacts';
import useHasLandscapeSections from '../hooks/useHasLandscapeSections.hook';
import { GENERIC_ERROR_MESSAGE, TERMINOLOGY } from '@shared/i18n';
import useIsVersionEditable from '../hooks/useIsVersionEditable.hook';
import useEditorActive from '../hooks/useEditorActive.hook';
import DocumentAttachmentsDialog from './dialogs/DocumentAttachmentsDialog';
import { ContentAutomationDialog, useContentAutomationNavAction } from '@modules/mocks';
import { AssignTrainingDialog } from '@modules/mocks/training';
import { AI_EDIT_REVIEW_LOCK_REASON } from './DocumentControlsLock';
import { isAiEditReviewActive } from '../tiptap/plugins/AiEditReviewPlugin';
import useEditNavActions from '../hooks/useEditNavActions.hook';
import useDocumentNavTabs from '../hooks/useDocumentNavTabs.hook';

type TAttachmentEntry = {
  attachmentId: string;
  attachment?: TDocumentAttachment;
};

const DocumentContainer = React.memo(() => {
  // console.log('re-rendering document container...');
  const {
    doc,
    permissions,
    resolvedItems,
    replica,
    version,
    isEdit,
    hasDraft,
    completeVersion,
    save,
    openDocumentConfigRef,
    hasUnsupportedContent,
  } = useContext(DocumentContext);
  const isAiEditLocked = useEditorActive((editor) => isAiEditReviewActive(editor.state), false);
  const { screenMode, viewChanges, setScreenMode } = useItemScreenStore();
  const itemType = 'DOCUMENT';
  const { organization } = useContext(SignInContext);
  const { canEdit, editLockedReason } = useCanEdit(permissions.accessRight);
  const { canEdit: canEditWithoutAuthorGroup } = useCanEdit(permissions.accessRight, { authorGroup: false });
  const isImpactAnalysisEnabled = organization.featureSettings.impactAnalysis?.enabled;
  const isExternalLinkEnabled = organization.featureSettings.externalLink?.enabled;
  const { show } = useContext(ToastContext);
  const { layoutState } = useContext(LayoutContext);

  const handleShowAccess = () =>
    setAccessDialogState({
      isOpen: true,
      id: doc._id,
      nodeType: 'DOCUMENT',
      name: doc.name,
    });

  // Document export functionality
  const handleExport = async () => {
    if (!version) return;

    if (isEdit) {
      const success = await save();
      if (!success) return show('error', 'Export failed, as changes could not be saved.');
    }

    const versionId = getVersionId(version);
    const onSuccess = (data: TDraft | TVersion) => {
      if (!data.pdfBlobName) {
        // TODO: Jan !isPdfUpToDate ||
        show('error', 'PDF is not available yet. Please try again later.');
        console.error('PDF is not available yet.', data);
        return;
      }
      downloadFile(
        `${config.AZURE_STORAGE_BASE_URL}/${doc._id}/${encodeURIComponent(data.pdfBlobName)}?${permissions.token}`,
        'application/pdf',
        getPdfFilename((replica || doc).name, doc.pdfExportFileNames?.[versionId])
      );
    };

    mutateExportDoc(
      {
        documentId: doc._id,
        versionId,
      },
      { onSuccess }
    );
  };

  // --- NAV:
  const navigate = useNavigate();
  const { change, changeMany, searchParams } = useSearchParams();
  const periodicReviewParam = searchParams.get(PERIODIC_REVIEW_QUERY_PARAM);

  const [accessDialogState, setAccessDialogState] = useState<TAccessDialogState | undefined>(undefined);
  const [optionsDialogState, setOptionsDialogState] = useState<TOptionsDialogState | undefined>(undefined);
  const [isOpenDocumentConfig, setIsOpenDocumentConfig] = useState(false);
  const [documentConfigInitialTab, setDocumentConfigInitialTab] = useState<EDocumentConfigTab | undefined>();
  const [isPeriodicReviewOpen, setIsPeriodicReviewOpen] = useState(false);

  useEffect(() => {
    if (!replica && periodicReviewParam === PERIODIC_REVIEW_QUERY_VALUE) setIsPeriodicReviewOpen(true);
  }, [periodicReviewParam, replica]);

  const handleSetPeriodicReviewOpen = (isOpen: boolean) => {
    setIsPeriodicReviewOpen(isOpen);
    if (!isOpen) changeMany({ [PERIODIC_REVIEW_QUERY_PARAM]: undefined });
  };

  const openPeriodicReviewDialog = () => {
    if (replica) return;
    setIsPeriodicReviewOpen(true);
    changeMany({ [PERIODIC_REVIEW_QUERY_PARAM]: PERIODIC_REVIEW_QUERY_VALUE });
  };

  // Set the ref so that CreateVersionDialog can open this dialog on missing tagged snippets error
  useEffect(() => {
    openDocumentConfigRef.current = (initialTab?: EDocumentConfigTab) => {
      setDocumentConfigInitialTab(initialTab);
      setIsOpenDocumentConfig(true);
    };
    return () => {
      openDocumentConfigRef.current = null;
    };
  }, [openDocumentConfigRef]);

  const [isCreateApprovalDialogOpen, setIsCreateApprovalDialogOpen] = useState(false);
  const [isAttachmentsDialogOpen, setIsAttachmentsDialogOpen] = useState(false);
  const [viewApprovalDialogState, setViewApprovalDialogState] = useState<TViewApprovalDialogState | undefined>(
    undefined
  );
  const [updateApprovalDialogState, setUpdateApprovalDialogState] = useState<TUpdateApprovalDialogState | undefined>(
    undefined
  );
  const { impactAnalysisNavActionButton } = useImpactAnalysisNavAction({ version, setOptionsDialogState, permissions });
  const { externalLinkNavActionButton } = useExternalLinkNavAction({ item: replica ?? doc });
  const { contentAutomationConfig, contentAutomationNavActionButton } = useContentAutomationNavAction();
  const hasLandscapeSections = useHasLandscapeSections();
  const documentNavTabs = useDocumentNavTabs();
  const versionId = version ? getVersionId(version) : (doc.versions[0]?.id ?? 'draft');
  const isVersionEditable = useIsVersionEditable(versionId, doc);
  const canEditAttachments = isVersionEditable && isTDraft(version) && canEdit && !replica;
  const attachmentsById = useMemo(
    () => new Map((doc.attachments ?? []).map((attachment) => [attachment._id, attachment])),
    [doc.attachments]
  );
  const viewedAttachmentIds = useMemo(() => (version ? (version.attachmentIds ?? []) : []), [version]);
  const attachmentEntries = useMemo<TAttachmentEntry[]>(
    () =>
      viewedAttachmentIds.map((attachmentId) => ({
        attachmentId,
        attachment: attachmentsById.get(attachmentId),
      })),
    [attachmentsById, viewedAttachmentIds]
  );
  const attachmentsDialogMode = canEditAttachments
    ? 'edit'
    : !doc.draft
      ? 'readonly-no-draft'
      : isTDraft(version)
        ? 'readonly-permissions'
        : 'readonly-version';

  // API hooks
  const { mutate: mutateExportDoc, isPending: isPendingExportDoc } = useExportDoc();

  const handleCompleteVersion = useCallback(() => {
    if (
      doc.versions.length > 0 &&
      doc.approvals.find((approval) => !approval.dateCompleted && approval.versionId === doc.versions[0].id)
    )
      show('warning', 'Document has a pending approval request!');
    else completeVersion();
  }, [doc.versions, doc.approvals, completeVersion]);
  const viewLatest = useCallback(() => change('view', 'latest'), [change]);

  const latestVersion = doc.draft || doc.versions[0];
  const isViewingLatest = !version || version === latestVersion;
  const editDisabledReason = replica
    ? `${TERMINOLOGY.synchronized.upper} documents can't be edited.`
    : hasUnsupportedContent
      ? GENERIC_ERROR_MESSAGE
      : editLockedReason;
  const { editAction, completeAction } = useEditNavActions({
    hasDraft,
    disabledReason: editDisabledReason,
    viewLatest: isViewingLatest ? undefined : viewLatest,
    onComplete: !replica && Boolean(doc.draft) ? handleCompleteVersion : undefined,
  });

  const navActions: TOverflowButtonParam[] = useMemo(() => {
    const navActionsList: TOverflowButtonParam[] = [];
    const applyAiEditReviewLock = (actions: TOverflowButtonParam[]) =>
      isAiEditLocked
        ? actions.map((action) =>
            action.value === 'export' || action.value === 'externalLink'
              ? action
              : {
                  ...action,
                  disabled: true,
                  disabledMessage: AI_EDIT_REVIEW_LOCK_REASON,
                }
          )
        : actions;

    if (viewChanges) {
      return applyAiEditReviewLock([
        {
          appearance: 'primary',
          children:
            screenMode &&
            [EItemScreenMode.POST_CHANGE_IMPACT_ANALYSIS, EItemScreenMode.IMPACT_ANALYSIS_SUMMARY].includes(screenMode)
              ? 'Close impact analysis'
              : 'Close changes',
          icon: <DismissFilled />,
          onClick: () => setScreenMode(),
        },
      ]);
    }

    if (replica)
      navActionsList.push({
        children: `View ${TERMINOLOGY.master.lower}`,
        icon: <OpenRegular />,
        onClick: () => navigate(`/document/${doc._id}`),
      });

    navActionsList.push(editAction);
    if (completeAction) navActionsList.push(completeAction);

    if (isImpactAnalysisEnabled) navActionsList.push(impactAnalysisNavActionButton);

    if (!replica && !doc.isBlock && contentAutomationNavActionButton) {
      navActionsList.push(contentAutomationNavActionButton);
    }

    if (
      !replica &&
      canEditWithoutAuthorGroup &&
      !doc.isBlock &&
      version &&
      !isTDraft(version) &&
      !doc.approvals.some(
        (approval) =>
          approval.versionId === version.id && (!approval.dateCompleted || approval.status === ApprovalStatus.APPROVED)
      )
    )
      navActionsList.push({
        children: 'Request approval',
        icon: <CheckboxPersonRegular />,
        onClick: () => setIsCreateApprovalDialogOpen(true),
      });

    if (version) {
      const hasPreviousVersion = isTDraft(version)
        ? doc.versions.length > 0
        : doc.versions.findIndex((v) => v.id === (version as TVersion).id) < doc.versions.length - 1;
      navActionsList.push({
        children: 'Changes',
        icon: navActionsList.length === 0 ? <HistoryFilled /> : <HistoryRegular />,
        onClick: () => setScreenMode(EItemScreenMode.CHANGES),
        disabled: !hasPreviousVersion,
        disabledMessage: !hasPreviousVersion
          ? isTDraft(version)
            ? 'Complete a version to compare changes.'
            : 'This is the first version.'
          : undefined,
      });
    }

    if (permissions.accessRight >= AccessType.MANAGE) {
      navActionsList.push({
        children: 'Manage access',
        icon: <PeopleRegular />,
        onClick: handleShowAccess,
      });
    }

    if (layoutState) {
      navActionsList.push({
        children: 'Rename',
        icon: <RenameRegular />,
        value: 'rename',
        disabled: !canEditWithoutAuthorGroup,
        onClick: layoutState.onRename,
      });
    }

    const versionLabel = version ? (isTDraft(version) ? 'draft' : `v${(version as TVersion).id}`) : '';

    navActionsList.push({
      children: `Download ${versionLabel}`,
      icon: isPendingExportDoc ? <Spinner size="tiny" /> : <ArrowDownloadRegular />,
      disabledMessage: doc.isBlock ? `${TERMINOLOGY.block.upper} cannot be exported.` : undefined,
      disabled: !version || doc.isBlock || isPendingExportDoc,
      onClick: handleExport,
      value: 'export',
    });

    if (isExternalLinkEnabled && !doc.isBlock) navActionsList.push(externalLinkNavActionButton);

    if (!replica) {
      navActionsList.push({
        children: 'Settings',
        value: 'settings',
        icon: <SettingsRegular />,
        onClick: () => {
          setDocumentConfigInitialTab(undefined);
          setIsOpenDocumentConfig(true);
        },
      });
    }

    return applyAiEditReviewLock(navActionsList);
  }, [
    version,
    editAction,
    completeAction,
    permissions,
    screenMode,
    viewChanges,
    doc.approvals,
    layoutState,
    isPendingExportDoc,
    isImpactAnalysisEnabled,
    contentAutomationNavActionButton,
    isAiEditLocked,
    isExternalLinkEnabled,
    externalLinkNavActionButton,
  ]);

  const navTabs: TMap<string, ReactNode> = {
    Overview: (
      <NavOverview
        item={doc}
        replica={replica}
        actions={navActions}
        itemVersion={version && { type: doc.type, item: doc, version }}
        permissions={permissions}
        setViewApprovalDialogState={setViewApprovalDialogState}
        openPeriodicReviewDialog={openPeriodicReviewDialog}
        extraContent={
          doc.attachments !== undefined ? (
            <p className="flex">
              <span className="block w-32 flex-shrink-0">Attachments:</span>
              <FluentLink onClick={() => setIsAttachmentsDialogOpen(true)}>{attachmentEntries.length}</FluentLink>
            </p>
          ) : undefined
        }
      />
    ),
    ...documentNavTabs,
  };

  const trainingApprovalVersionId = doc.approvals.find(
    (approval) => approval._id === viewApprovalDialogState?.approvalId
  )?.versionId;
  const trainingVersion =
    doc.versions.find((candidate) => candidate.id === trainingApprovalVersionId) ??
    (version && !isTDraft(version) ? version : doc.versions[0]);

  return (
    <LinkMenuProvider>
      <ContentAutomationDialog config={contentAutomationConfig} currentDocumentId={doc._id} />
      <AssignTrainingDialog documentId={doc._id} documentName={doc.name} versionId={trainingVersion?.id ?? ''} />
      <DocumentAttachmentsDialog
        open={isAttachmentsDialogOpen}
        setOpen={setIsAttachmentsDialogOpen}
        documentId={doc._id}
        token={permissions.token}
        entries={attachmentEntries}
        mode={attachmentsDialogMode}
      />
      <AccessDialog state={accessDialogState} setState={setAccessDialogState} />
      <OptionsDialog state={optionsDialogState} setState={setOptionsDialogState} />
      <ItemConfigDialog
        isOpen={isOpenDocumentConfig}
        setIsOpen={setIsOpenDocumentConfig}
        item={doc}
        isDoc
        version={version}
        permissions={permissions}
        initialTab={documentConfigInitialTab}
        openPeriodicReviewDialog={openPeriodicReviewDialog}
      />
      {!replica && (
        <PeriodicReviewDialog
          isOpen={isPeriodicReviewOpen}
          setIsOpen={handleSetPeriodicReviewOpen}
          item={doc}
          itemType="DOCUMENT"
          permissions={permissions}
        />
      )}
      <CreateApprovalDialog
        isOpen={isCreateApprovalDialogOpen}
        setIsOpen={setIsCreateApprovalDialogOpen}
        master={doc}
        itemType={itemType}
        versionId={version && !isTDraft(version) ? version.id : undefined}
        setViewApprovalDialogState={setViewApprovalDialogState}
      />
      <ViewApprovalDialog
        state={viewApprovalDialogState}
        setState={setViewApprovalDialogState}
        setUpdateApprovalDialogState={setUpdateApprovalDialogState}
        item={doc}
        replica={replica}
        itemType={itemType}
        permissions={permissions}
      />
      <UpdateApprovalDialog
        state={updateApprovalDialogState}
        setState={setUpdateApprovalDialogState}
        setViewApprovalDialogState={setViewApprovalDialogState}
        item={doc}
        itemType={itemType}
      />
      <ItemPage
        item={doc}
        permissions={permissions}
        resolvedItems={resolvedItems}
        version={version}
        replica={replica}
        navTabs={navTabs}
        renderHeader={(selectedTab, setSelectedTab) => (
          <Header
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            // Access dialog handler
            handleShowAccess={handleShowAccess}
          />
        )}
        content={<Content />}
        setViewApprovalDialogState={setViewApprovalDialogState}
        hasLandscapeSections={hasLandscapeSections}
      />
    </LinkMenuProvider>
  );
});

export default DocumentContainer;
